import { Injectable, effect, inject, signal } from '@angular/core';
import { ORG_ID, SessionDto } from '@data-access/bulk-operations-api';
import { injectFeatureFlag } from '@feature-flag';
import { take } from 'rxjs';

import { environment } from '../../../environments/environment';
import { UserSessionService } from './user-session.service';

export enum SurveyEvent {
  EmployeeCard = 'show_di_employee_card',
}

@Injectable({ providedIn: 'root' })
export class SurvicateService {
  private readonly userSessionService = inject(UserSessionService);
  private readonly orgId = inject(ORG_ID);
  // TODO: remove when 'enable-survicate-tool' is removed
  private readonly isEnabledSignal = injectFeatureFlag('enable-survicate-tool');
  private readonly pendingEventsSignal = signal<SurveyEvent[]>([]);
  private readonly initializedSignal = signal(false);

  constructor() {
    // TODO: extract to init() method and call from AppComponent when 'enable-survicate-tool' is removed
    effect(() => {
      const workspaceKey = environment.survicateWorkspaceKey;
      if (!this.isEnabledSignal() || !workspaceKey || this.initializedSignal()) {
        return;
      }

      this.userSessionService
        .getSession()
        .pipe(take(1))
        .subscribe((session) => {
          void this.initSurvicate(session, workspaceKey);
        });
    });
  }

  triggerSurvey(event: SurveyEvent): void {
    if (!this.initializedSignal()) {
      this.pendingEventsSignal.update((events) => [...events, event]);
      return;
    }

    this.invokeEvent(event);
  }

  private async initSurvicate(session: SessionDto, workspaceKey: string): Promise<void> {
    const { default: Survicate } =
      await import('@survicate/survicate-web-package/survicate_widget');

    const company = session.availableContexts?.find((ctx) => ctx.id.toString() === this.orgId);

    const traits: Record<string, string> = {
      email: session.emailAddress,
      user_id: session.emailAddress,
      company_name: company?.name ?? '',
      country: session.countryCode,
      language: session.languageCode,
    };

    try {
      await Survicate.init({
        workspaceKey,
        traits,
      });

      this.initializedSignal.set(true);
      this.flushPendingEvents();
    } catch (error: unknown) {
      console.error('Survicate initialization failed', error);
    }
  }

  private flushPendingEvents(): void {
    const events = this.pendingEventsSignal();
    this.pendingEventsSignal.set([]);
    events.forEach((event) => this.invokeEvent(event));
  }

  private invokeEvent(event: SurveyEvent): void {
    void import('@survicate/survicate-web-package/survicate_widget')
      .then(({ default: Survicate }) => {
        Survicate.invokeEvent(event);
      })
      .catch((error) => {
        console.error('Survicate SDK failed to load', error);
      });
  }
}
